/**
 * Public print views — the DIGITAL receipt's client service.
 *
 * Mirrors `apps/api/components/routes/public/print-records.ts`:
 *
 *   GET /public/print-records/:token → `getPrintView`
 *
 * There is NO auth here, for the same reason as `publicChecks.ts`: a guest (or
 * an operator testing receipts with no printer) arrives with a signed print
 * view token and nothing else, so the call goes through the Firebase-free
 * public client and the page (`apps/checkout` `/receipt/:token`) mounts outside
 * `<CustomerSession>`.
 *
 * Every miss — malformed, tampered or expired token, unknown record, a record
 * with no composed document — is ONE byte-identical 404, so there is nothing
 * in the body to branch on: {@link isPrintViewMiss} reads the status.
 */

import type { PrintDocument } from '@order-up/models';

import { createPublicApiClient, unwrapPublicEnvelope, type PublicApiError } from './publicApiClient';

const publicClient = createPublicApiClient();

/** One composed print, as the route answers it. */
export interface PrintView {
  /** The print type (`customer_receipt`, `guest_check`, …). */
  readonly type: string;
  /** True when it was a digital print — stored, never sent to a printer. */
  readonly digital?: boolean;
  /** The paper the document is drawn on. */
  readonly paperWidthMm: 58 | 76 | 80;
  readonly orderNumber?: number;
  readonly createdAtMs?: number;
  /** The document the printer would have printed — drawn by the paper preview. */
  readonly document: PrintDocument;
}

export async function getPrintView(token: string): Promise<PrintView> {
  return unwrapPublicEnvelope<PrintView>(publicClient.get(`/print-records/${encodeURIComponent(token)}`));
}

/** The link is dead (or never was): the page's state, not a failure to retry. */
export function isPrintViewMiss(error: unknown): boolean {
  const status = (error as PublicApiError | undefined)?.status;
  return status === 404 || status === 414;
}

export const publicPrintViewsApi = { getPrintView };
